import React, { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import './os.css';
import {
  ContextTabs, StatusBadge, MetricCard, DataTable, ViewToggle, DetailDrawer,
  EmptyState, LoadingState, ErrorState, UnknownState, ActionBar,
} from './osPrimitives';
import { Download, ExternalLink, FileText } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const money = (n) => (n === undefined || n === null || n === '') ? '—' : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const humanize = (s) => (s || '').toString().replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const authHeaders = () => { const t = localStorage.getItem('ace_admin_token'); return { ...(t ? { Authorization: `Bearer ${t}` } : {}) }; };
async function getJSON(path) { const r = await fetch(`${API}${path}`, { credentials: 'include', headers: authHeaders() }); if (!r.ok) throw new Error(`${path} ${r.status}`); return r.json(); }
function useLoad(path) {
  const [s, setS] = React.useState({ loading: true });
  const load = React.useCallback(() => { setS({ loading: true }); getJSON(path).then((d) => setS({ loading: false, d })).catch(() => setS({ loading: false, error: true })); }, [path]);
  React.useEffect(() => { load(); }, [load]);
  return { ...s, reload: load };
}
const fmtDate = (v) => { if (!v) return '—'; const d = new Date(v); return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); };
const INV_TONE = { paid: 'ready', sent: 'waiting', draft: 'unknown', overdue: 'risk', partial: 'attention', unpaid: 'waiting' };
const REC_STATUS = { NOT_READY: 'unknown', READY: 'waiting', HOLD_REVIEW: 'attention', DISPUTED: 'risk', APPROVED: 'ready', SCHEDULED: 'waiting', PAID: 'ready' };

const TABS = [
  { id: 'money', label: 'Money' }, { id: 'revenue', label: 'Revenue' }, { id: 'payouts', label: 'Payouts' },
  { id: 'performance', label: 'Performance' }, { id: 'growth', label: 'Growth' }, { id: 'reports', label: 'Reports' },
];

export default function BusinessLive() {
  const [params, setParams] = useSearchParams();
  const initial = TABS.some((t) => t.id === params.get('tab')) ? params.get('tab') : 'money';
  const [tab, setTab] = useState(initial);
  const navigate = useNavigate();
  const change = (t) => { setTab(t); setParams({ tab: t }, { replace: true }); };
  return (
    <div className="os-animate" data-testid="business-live">
      <ContextTabs tabs={TABS} active={tab} onChange={change} testid="business-tabs" />
      <div className="mt-4">
        {tab === 'money' && <MoneyTab navigate={navigate} />}
        {tab === 'revenue' && <RevenueTab navigate={navigate} />}
        {tab === 'payouts' && <PayoutsTab navigate={navigate} />}
        {tab === 'performance' && <PerformanceTab navigate={navigate} />}
        {tab === 'growth' && <GrowthTab navigate={navigate} />}
        {tab === 'reports' && <ReportsTab />}
      </div>
    </div>
  );
}

/* ── MONEY ── */
function MoneyTab({ navigate }) {
  const inv = useLoad('/api/command-center/invoices');
  const tx = useLoad('/api/payments/transactions');
  const [view, setView] = useState('cards');
  const [peek, setPeek] = useState(null);
  if (inv.loading) return <LoadingState rows={4} />;
  if (inv.error) return <ErrorState body="Finance data is unavailable." onRetry={inv.reload} />;
  const invoices = inv.d.invoices || [];
  const txns = Array.isArray(tx.d) ? tx.d : (tx.d?.transactions || []);
  const outstanding = inv.d.outstanding_total ?? ((Number(inv.d.billed) || 0) - (Number(inv.d.collected) || 0));
  return (
    <div data-testid="business-money">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-4">
        <MetricCard label="Billed" value={money(inv.d.billed)} testid="money-billed" />
        <MetricCard label="Collected" value={money(inv.d.collected)} testid="money-collected" />
        <MetricCard label="Outstanding" value={money(outstanding)} testid="money-unpaid" />
        <MetricCard label={`Invoices (${inv.d.unpaid ?? 0} unpaid)`} value={inv.d.total ?? invoices.length} testid="money-count" />
      </div>
      <p className="text-[11px] mb-3" style={{ color: 'var(--os-muted)' }}>Billed & Collected are authoritative backend totals; Outstanding is billed − collected (no per-item recomputation).</p>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-[10px] uppercase tracking-widest font-bold" style={{ color: 'var(--os-muted)' }}>Invoices</h3>
        <ViewToggle view={view} onChange={setView} testid="money-view" />
      </div>
      {!invoices.length ? <EmptyState title="No invoices on record" /> :
        view === 'table' ? <DataTable testid="money-invoices-table"
          columns={[{ key: 'invoice_id', label: 'Invoice' }, { key: 'bill_to', label: 'Bill to' }, { key: 'status', label: 'Status', render: (r) => <StatusBadge status={INV_TONE[r.status] || 'unknown'} label={humanize(r.status)} /> }, { key: 'subtotal', label: 'Amount', render: (r) => money(r.subtotal) }, { key: 'balance_due', label: 'Balance', render: (r) => money(r.balance_due) }]}
          rows={invoices} onRow={setPeek} />
        : <div className={view === 'compact' ? '' : 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3'} data-testid="money-invoices-list">
          {invoices.map((i) => <div key={i.invoice_id} className="os-card p-4 cursor-pointer" onClick={() => setPeek(i)} data-testid={`money-invoice-${i.invoice_id}`}>
            <div className="flex items-center justify-between"><StatusBadge status={INV_TONE[i.status] || 'unknown'} label={humanize(i.status)} /><span className="text-[10px] font-mono" style={{ color: 'var(--os-muted)' }}>{i.invoice_id}</span></div>
            <h3 className="font-bold mt-2 truncate" style={{ color: 'var(--os-text)' }}>{i.bill_to || 'Client'}</h3>
            <p className="text-xs" style={{ color: 'var(--os-muted)' }}>{i.booking_ref || '—'} · {fmtDate(i.invoice_date || i.created_at)}</p>
            <div className="flex justify-between mt-2 text-sm"><span style={{ color: 'var(--os-muted)' }}>Amount {money(i.subtotal)}</span><span style={{ color: (i.balance_due || 0) > 0 ? 'var(--os-warning)' : 'var(--os-success)' }}>Bal {money(i.balance_due)}</span></div>
          </div>)}
        </div>}
      <div className="mt-5">
        <h3 className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: 'var(--os-muted)' }}>Recent payment activity</h3>
        {tx.loading ? <LoadingState rows={2} /> : !txns.length ? <p className="text-sm" style={{ color: 'var(--os-muted)' }}>No payment transactions recorded.</p>
          : txns.slice(0, 12).map((t) => <div key={t.payment_id} className="flex items-center justify-between py-1.5 border-b" style={{ borderColor: 'var(--os-border)' }} data-testid={`money-txn-${t.payment_id}`}>
            <div className="min-w-0"><span className="text-sm" style={{ color: 'var(--os-text)' }}>{t.customer_name || t.booking_id || 'Payment'}</span><p className="text-[11px]" style={{ color: 'var(--os-muted)' }}>{fmtDate(t.created_at)} · {t.booking_id || ''}</p></div>
            <div className="text-right"><StatusBadge status={(t.payment_status === 'paid' || t.status === 'complete') ? 'ready' : (t.payment_status === 'failed' ? 'risk' : 'waiting')} label={humanize(t.payment_status || t.status)} /><p className="text-sm mt-0.5" style={{ color: 'var(--os-text)' }}>{money(t.amount)}</p></div>
          </div>)}
      </div>
      <DetailDrawer open={!!peek} title={peek?.invoice_id} subtitle={peek ? `Invoice · ${peek.bill_to || ''}` : ''} onClose={() => setPeek(null)} testid="money-invoice-peek"
        footer={peek?.booking_ref ? <ActionBar primary={{ label: 'Open Service Record', onClick: () => navigate(`/os/services/${peek.booking_ref}`) }} /> : null}>
        {peek && <div>
          <div className="flex gap-2 mb-3"><StatusBadge status={INV_TONE[peek.status] || 'unknown'} label={humanize(peek.status)} /></div>
          <PF label="Bill to" value={peek.bill_to} /><PF label="Linked booking" value={peek.booking_ref} />
          <PF label="Amount" value={money(peek.subtotal)} /><PF label="Deposit paid" value={money(peek.deposit_paid)} /><PF label="Balance due" value={money(peek.balance_due)} />
          <PF label="Invoice date" value={fmtDate(peek.invoice_date)} /><PF label="Due date" value={fmtDate(peek.due_date)} />
          <PF label="Email status" value={peek.email_sent ? `Sent · ${humanize(peek.email_status || 'ok')}` : 'Not sent'} />
          <p className="text-[11px] mt-2" style={{ color: 'var(--os-muted)' }}>Balances come from the invoice service — not recomputed here.</p>
        </div>}
      </DetailDrawer>
    </div>
  );
}

/* ── REVENUE ── */
function RevenueTab({ navigate }) {
  const inv = useLoad('/api/command-center/invoices');
  const kpi = useLoad('/api/crm/kpis');
  const quotes = useLoad('/api/command-center/quote-log');
  if (inv.loading || kpi.loading) return <LoadingState rows={4} />;
  const segs = kpi.d?.segments || {};
  const vip = kpi.d?.vip_counts || {};
  return (
    <div data-testid="business-revenue">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-4">
        <MetricCard label="Billed (all-time)" value={money(inv.d?.billed)} testid="rev-billed" />
        <MetricCard label="Collected" value={money(inv.d?.collected)} testid="rev-collected" />
        <MetricCard label="Client LTV (total)" value={money(kpi.d?.total_ltv)} testid="rev-ltv" />
        <MetricCard label="Quotes in pipeline" value={quotes.d?.total ?? '—'} testid="rev-quotes" />
      </div>
      <p className="text-[11px] mb-4" style={{ color: 'var(--os-muted)' }}>Figures are returned by the invoice + CRM services. No revenue/margin math is performed in the browser.</p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="os-card p-4" data-testid="rev-segments">
          <h3 className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: 'var(--os-muted)' }}>Client segments</h3>
          {Object.keys(segs).length ? Object.entries(segs).map(([k, v]) => <div key={k} className="flex justify-between py-1"><span className="text-sm" style={{ color: 'var(--os-text)' }}>{humanize(k)}</span><span className="text-sm" style={{ color: 'var(--os-muted)' }}>{v}</span></div>) : <UnknownState title="No segment data" />}
        </div>
        <div className="os-card p-4" data-testid="rev-members">
          <h3 className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: 'var(--os-muted)' }}>Membership mix (VIP)</h3>
          {Object.keys(vip).length ? Object.entries(vip).map(([k, v]) => <div key={k} className="flex justify-between py-1"><span className="text-sm" style={{ color: 'var(--os-text)' }}>{humanize(k)}</span><span className="text-sm" style={{ color: 'var(--os-muted)' }}>{v}</span></div>) : <UnknownState title="No membership data" />}
          <div className="flex justify-between py-1 border-t mt-1" style={{ borderColor: 'var(--os-border)' }}><span className="text-sm" style={{ color: 'var(--os-muted)' }}>Total clients</span><span className="text-sm" style={{ color: 'var(--os-text)' }}>{kpi.d?.total_clients ?? '—'}</span></div>
        </div>
      </div>
    </div>
  );
}

/* ── PAYOUTS ── */
const PAY_FILTERS = ['READY', 'APPROVED', 'SCHEDULED', 'PAID', 'DISPUTED'];
function PayoutsTab({ navigate }) {
  const recs = useLoad('/api/fulfillment/control/reconciliations');
  const providers = useLoad('/api/fulfillment/control/providers');
  const [filter, setFilter] = useState('READY');
  const nameMap = useMemo(() => Object.fromEntries((providers.d?.providers || []).map((p) => [p.provider_id, { name: p.provider_name, type: p.provider_type }])), [providers.d]);
  if (recs.loading) return <LoadingState rows={3} />;
  if (recs.error) return <ErrorState body="Reconciliation records are unavailable." onRetry={recs.reload} />;
  const all = recs.d.reconciliations || [];
  const counts = all.reduce((a, r) => { a[r.status] = (a[r.status] || 0) + 1; return a; }, {});
  const rows = all.filter((r) => r.status === filter);
  const kindOf = (pid) => { const t = nameMap[pid]?.type; return t === 'affiliate_partner' ? 'Affiliate payout' : t === 'fleet_owner' ? 'Fleet-owner payout' : (String(pid).startsWith('DRV-') ? 'Driver payout' : 'Driven Collective payout'); };
  return (
    <div data-testid="business-payouts">
      <ContextTabs tabs={PAY_FILTERS.map((f) => ({ id: f, label: humanize(f), count: counts[f] || 0 }))} active={filter} onChange={setFilter} testid="payouts-filters" />
      <p className="text-[11px] my-3" style={{ color: 'var(--os-muted)' }}>Reuses the canonical REC-* reconciliation state (Driven Collective / affiliate / fleet-owner / driver payouts are kept distinct). Partner cost is staff-only; client price & ACE margin are never shown. PAID requires Finance evidence in the canonical workflow.</p>
      {!rows.length ? <EmptyState title={`No ${humanize(filter).toLowerCase()} reconciliations`} />
        : <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="payouts-list">
          {rows.map((r) => <div key={r.reconciliation_id} className="os-card p-4" data-testid={`payout-${r.reconciliation_id}`}>
            <div className="flex items-center justify-between"><StatusBadge status={REC_STATUS[r.status] || 'unknown'} label={humanize(r.status)} /><span className="os-chip" style={{ color: 'var(--os-muted)', border: '1px solid var(--os-border)' }}>{kindOf(r.provider_id)}</span></div>
            <h3 className="font-bold mt-2" style={{ color: 'var(--os-text)' }}>{nameMap[r.provider_id]?.name || r.provider_id}</h3>
            <p className="text-xs" style={{ color: 'var(--os-muted)' }}>{r.reconciliation_id}{r.booking_id ? ` · ${r.booking_id}` : ''}</p>
            <div className="flex justify-between mt-2 text-sm"><span style={{ color: 'var(--os-muted)' }}>Payable</span><span style={{ color: 'var(--os-red)' }}>{money(r.gross_payable)}</span></div>
            <div className="flex gap-2 mt-3">
              <button className="os-btn-ghost" style={{ minHeight: 32, padding: '5px 10px' }} onClick={() => navigate(`/os/network?rec=${r.reconciliation_id}`)} data-testid={`payout-open-${r.reconciliation_id}`}><ExternalLink className="h-3.5 w-3.5" /> Open in Network</button>
              {nameMap[r.provider_id] && <button className="os-btn-ghost" style={{ minHeight: 32, padding: '5px 10px' }} onClick={() => navigate(`/os/network/partners/${r.provider_id}`)} data-testid={`payout-partner-${r.reconciliation_id}`}>Partner →</button>}
            </div>
          </div>)}
        </div>}
    </div>
  );
}

/* ── PERFORMANCE ── */
function PerformanceTab({ navigate }) {
  const inv = useLoad('/api/command-center/invoices');
  const providers = useLoad('/api/fulfillment/control/providers');
  const kpi = useLoad('/api/crm/kpis');
  if (inv.loading || providers.loading) return <LoadingState rows={3} />;
  const provs = providers.d?.providers || [];
  return (
    <div data-testid="business-performance">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-4">
        <MetricCard label="Collected" value={money(inv.d?.collected)} testid="perf-collected" />
        <MetricCard label="Outstanding" value={money(inv.d?.outstanding_total ?? ((Number(inv.d?.billed) || 0) - (Number(inv.d?.collected) || 0)))} testid="perf-unpaid" />
        <MetricCard label="Active clients" value={kpi.d?.total_clients ?? '—'} testid="perf-clients" />
        <MetricCard label="Providers" value={provs.length} testid="perf-providers" />
      </div>
      <p className="text-[11px] mb-3" style={{ color: 'var(--os-muted)' }}>Discrete canonical metrics only — no synthetic 1–100 business score. Provider metrics preserve their P7 sample size / window.</p>
      <h3 className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: 'var(--os-muted)' }}>Provider performance (P7 read model)</h3>
      {!provs.length ? <EmptyState title="No providers" /> : provs.map((p) => {
        const perf = p.performance || {};
        return <div key={p.provider_id} className="os-card p-4 mb-2 cursor-pointer" onClick={() => navigate(`/os/network/partners/${p.provider_id}`)} data-testid={`perf-provider-${p.provider_id}`}>
          <div className="flex items-center justify-between"><h4 className="font-bold" style={{ color: 'var(--os-text)' }}>{p.provider_name}</h4><span className="os-chip" style={{ color: 'var(--os-muted)', border: '1px solid var(--os-border)' }}>{p.provider_type === 'affiliate_partner' ? 'AFF' : 'OWN'}</span></div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs" style={{ color: 'var(--os-muted)' }}>
            <span>Completion: {perf.completion_rate?.value != null ? `${Math.round(perf.completion_rate.value * 100)}% (n=${perf.completion_rate.sample_size ?? 0})` : '—'}</span>
            <span>Acceptance: {perf.acceptance_rate?.value != null ? `${Math.round(perf.acceptance_rate.value * 100)}% (n=${perf.acceptance_rate.sample_size ?? 0})` : '—'}</span>
            <span>Disputes: {perf.dispute_rate?.value != null ? `${Math.round(perf.dispute_rate.value * 100)}% (n=${perf.dispute_rate.sample_size ?? 0})` : '—'}</span>
          </div>
        </div>;
      })}
    </div>
  );
}

/* ── GROWTH (INQ → QT → BK chain) ── */
function GrowthTab({ navigate }) {
  const inq = useLoad('/api/ops/inquiries');
  const quotes = useLoad('/api/command-center/quote-log');
  const kpi = useLoad('/api/crm/kpis');
  if (inq.loading || quotes.loading) return <LoadingState rows={3} />;
  const leads = inq.d?.inquiries || inq.d || [];
  const qs = quotes.d?.quotes || [];
  const converted = qs.filter((q) => ['converted', 'confirmed'].includes((q.status || '').toLowerCase()) || q.booking_id || q.converted_booking_id);
  const vip = kpi.d?.vip_counts || {};
  const memberCount = Object.values(vip).reduce((a, b) => a + (Number(b) || 0), 0);
  return (
    <div data-testid="business-growth">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 mb-4">
        <MetricCard label="Leads (INQ)" value={Array.isArray(leads) ? leads.length : '—'} testid="growth-leads" />
        <MetricCard label="Quotes (QT)" value={quotes.d?.total ?? qs.length} testid="growth-quotes" />
        <MetricCard label="Converted (BK)" value={converted.length} testid="growth-converted" />
        <MetricCard label="Members" value={memberCount || '—'} testid="growth-members" />
        <MetricCard label="Clients" value={kpi.d?.total_clients ?? '—'} testid="growth-clients" />
      </div>
      <p className="text-[11px] mb-3" style={{ color: 'var(--os-muted)' }}>Preserves the canonical INQ-* → QT-* → BK-* chain. No new funnel model.</p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="os-card p-4" data-testid="growth-quote-list">
          <h3 className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: 'var(--os-muted)' }}>Recent quotes</h3>
          {!qs.length ? <EmptyState title="No quotes" /> : qs.slice(0, 10).map((q) => <div key={q.quote_id || q.id} className="flex items-center justify-between py-1.5 border-b" style={{ borderColor: 'var(--os-border)' }}>
            <div className="min-w-0"><span className="text-sm" style={{ color: 'var(--os-text)' }}>{q.client_name || q.customer_name || 'Quote'}</span><p className="text-[11px]" style={{ color: 'var(--os-muted)' }}>{q.quote_id || q.id} · {humanize(q.status)}</p></div>
            {(q.converted_booking_id || q.booking_id) && <button className="os-btn-ghost" style={{ minHeight: 30, padding: '4px 10px' }} onClick={() => navigate(`/os/services/${q.converted_booking_id || q.booking_id}`)} data-testid={`growth-quote-bk-${q.quote_id || q.id}`}>BK →</button>}
          </div>)}
        </div>
        <div className="os-card p-4" data-testid="growth-leads-list">
          <h3 className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: 'var(--os-muted)' }}>Recent leads</h3>
          {!Array.isArray(leads) || !leads.length ? <EmptyState title="No inquiries" /> : leads.slice(0, 10).map((l, i) => <div key={l.inquiry_id || i} className="py-1.5 border-b" style={{ borderColor: 'var(--os-border)' }}>
            <span className="text-sm" style={{ color: 'var(--os-text)' }}>{l.client_name || l.name || l.customer_name || 'Inquiry'}</span>
            <p className="text-[11px]" style={{ color: 'var(--os-muted)' }}>{l.inquiry_id || ''} · {humanize(l.status)}</p>
          </div>)}
        </div>
      </div>
    </div>
  );
}

/* ── REPORTS (existing exports only) ── */
function ReportsTab() {
  const [busy, setBusy] = useState('');
  const download = async (path, filename) => {
    setBusy(path);
    try {
      const r = await fetch(`${API}${path}`, { credentials: 'include', headers: authHeaders() });
      if (!r.ok) throw new Error(`Export ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch (e) { toast.error(e.message); } finally { setBusy(''); }
  };
  const reports = [
    { label: 'Bookings (CSV)', path: '/api/pricing/export/bookings.csv', file: 'bookings.csv' },
    { label: 'Vehicles (CSV)', path: '/api/pricing/export/vehicles.csv', file: 'vehicles.csv' },
    { label: 'Affiliates (CSV)', path: '/api/pricing/export/affiliates.csv', file: 'affiliates.csv' },
    { label: 'Monthly summary (CSV)', path: '/api/pricing/export/monthly.csv', file: 'monthly.csv' },
    { label: 'Bookings report (PDF)', path: '/api/pricing/export/bookings.pdf', file: 'bookings.pdf' },
  ];
  return (
    <div data-testid="business-reports">
      <p className="text-[11px] mb-3" style={{ color: 'var(--os-muted)' }}>Existing canonical exports — report generation is not rebuilt here. Client statements are available per-client from Client 360.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {reports.map((r) => <button key={r.path} className="os-card p-4 text-left flex items-center justify-between" onClick={() => download(r.path, r.file)} data-testid={`report-${r.file}`}>
          <span className="inline-flex items-center gap-2 text-sm" style={{ color: 'var(--os-text)' }}><FileText className="h-4 w-4" style={{ color: 'var(--os-red)' }} /> {r.label}</span>
          {busy === r.path ? <span className="text-[11px]" style={{ color: 'var(--os-muted)' }}>…</span> : <Download className="h-4 w-4" style={{ color: 'var(--os-muted)' }} />}
        </button>)}
      </div>
    </div>
  );
}

const PF = ({ label, value }) => (
  <div className="mb-3"><p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: 'var(--os-muted)' }}>{label}</p>
    <p className="text-sm" style={{ color: 'var(--os-text)' }}>{value === undefined || value === null || value === '' ? '—' : value}</p></div>
);
